'use client';
import { useEffect, useState } from 'react';
import { Content, Portal, Overlay } from '@radix-ui/react-dialog';

import DatePickerSucessMessage from './SuccessMessage';
import BookingCalendar from './BookingCalendar';
import DatePickerHeader from './Header';
import DatePickerFooter from './Footer';
import DatePickerError from './Error';
import Calendar from './Calendar';
import Summary from './Summary';
import XIcon from '../Icons/X';

import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import {
  Village,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';
import { cn } from '@/lib/utils';
import translateFromArray from '@/utils/get-translated-object';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import useVillageContext from '@/hooks/useVillageContext';
import useVillageDates from '@/hooks/useVillageDates';
import { useLocale } from '@/hooks/useLocale';

import './styles.css';

export type DatePickerExtraDicts = {
  datePickerDict: IDatePickerDictonary;
  enquiryDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  modalsDict: IModalsDictionary;
};

type DatePickerContentProps = {
  datePickerIsOpen?: boolean;
  confirmDates: (value: { start: Date | null; end: Date | null }) => void;
  confirmedDates: { start: Date | null; end: Date | null };
  onOpenChange: (open: boolean) => void;
  dict: IDatePickerDictonary;
  showSummary?: boolean;
  summaryContainerClassname?: string;
  showCalendarLegend?: boolean;
  village?: Village;
  experienceId?: number;
  onPeriodChange?: (
    period: VillagePricePeriod,
    variant: VillagePriceVariants,
  ) => void;
  isOneDate?: boolean;
  inVillaRange?: { start: Date; end: Date } | null;
  totalExperienceRange?: { start: Date; end: Date } | null;
  extraDicts?: DatePickerExtraDicts;
  skipCheckInDaysValidation?: boolean;
  onClearDates?: () => void;
};

export default function DatePickerContent({
  datePickerIsOpen,
  confirmDates,
  confirmedDates,
  onOpenChange,
  dict,
  showSummary,
  summaryContainerClassname,
  showCalendarLegend,
  village,
  experienceId,
  onPeriodChange,
  isOneDate = false,
  inVillaRange,
  totalExperienceRange,
  extraDicts,
  skipCheckInDaysValidation,
  onClearDates = () => {},
}: DatePickerContentProps) {
  const lang = useLocale();
  const {
    setCalendarPromotionTitle,
    totalBedrooms,
    totalGuests,
    visibleDeal,
    selectedDates,
    handleSelectDates,
  } = useVillageContext();

  const { enquiryModalState, setEnquiryModalState, clearCalendarFocus } =
    useDatePickerContext();

  const { setCalendarErrorTypes, calendarErrorTypes } = useDatePickerContext();
  const [selectedPeriod, setSelectedPeriod] = useState<VillagePricePeriod>();
  const [willUnmount, setWillUnmount] = useState(false);
  const [rangeValue, setRangeValue] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });

  const { getPriceVariantAndPeriod } = useVillageDates();

  const enquiryModalIsOpen = enquiryModalState === 'inside-booking-calendar';
  const canShowPromotionMessage = !!village;
  const canRenderError = showSummary;
  const shouldInvalidateDates =
    !!village || !!inVillaRange || !!totalExperienceRange;

  const handleClearDates = () => {
    clearCalendarFocus(rangeValue);

    handleSelectDates({ start: null, end: null });
    setRangeValue({ start: null, end: null });
    setSelectedPeriod(undefined);
    setCalendarErrorTypes([]);
    setCalendarPromotionTitle(undefined);
    onClearDates();
  };

  const handleConfirmDates = () => {
    if (!rangeValue.start || !rangeValue.end) {
      confirmDates({ start: null, end: null });
    }
    confirmDates({ start: rangeValue.start, end: rangeValue.end });
    handleCloseDatePicker();
  };

  const handleOpenAutoFocus = () => {
    setWillUnmount(false);
    const previewIsNull = rangeValue.start == null && rangeValue.end == null;
    const datesAlreadyConfirmed = confirmedDates?.start && confirmedDates?.end;
    if (previewIsNull && datesAlreadyConfirmed) {
      setRangeValue(confirmedDates);
    }
  };

  const handleCloseDatePicker = () => {
    setWillUnmount(true);
    setTimeout(() => {
      onOpenChange(false);
    }, 200);
    const noDatesAreSelected =
      rangeValue.start == null && rangeValue.end == null;
    const canClearCalendarErrors = calendarErrorTypes.length > 0;
    if (noDatesAreSelected) {
      if (canClearCalendarErrors) {
        setCalendarErrorTypes([]);
      }
    }
  };

  useEffect(() => {
    if (village) {
      const result = getPriceVariantAndPeriod({
        priceVariants: village.priceVariants,
        selectedDates: rangeValue,
        totalBedrooms,
        totalGuests,
      });

      if (result) {
        const { pricePeriod, priceVariant } = result;

        if (onPeriodChange) {
          if (pricePeriod) {
            onPeriodChange(pricePeriod, priceVariant);
            confirmDates(rangeValue);
          }
        }
      }
    }

    if (isOneDate) {
      confirmDates({ start: rangeValue.start, end: rangeValue.end });
      handleCloseDatePicker();
    }
    // eslint-disable-next-line
  }, [rangeValue]);

  useEffect(() => {
    if (visibleDeal) {
      const dealTranslation = translateFromArray({
        lang,
        translations: visibleDeal.deal.translation,
      });
      setCalendarPromotionTitle(dealTranslation?.descriptionCalendar);
    } else {
      setCalendarPromotionTitle(undefined);
    }
    // eslint-disable-next-line
  }, [visibleDeal]);

  useEffect(() => {
    const datesNotConfirmed =
      selectedDates.start == null && selectedDates.end == null;
    const previewIsSelected =
      rangeValue.start !== null && rangeValue.end !== null;

    if (datesNotConfirmed && previewIsSelected) {
      setRangeValue({ start: null, end: null });
    }
    // eslint-disable-next-line
  }, [selectedDates]);

  return (
    <Portal>
      <Overlay className="DatePickerOverlay">
        {showSummary ? (
          <div
            className={cn('WithSummaryContainer', summaryContainerClassname, {
              UnmountDatePicker: willUnmount,
            })}>
            <Summary showCalendarLegend={showCalendarLegend} dict={dict} />

            <Content
              onOpenAutoFocus={handleOpenAutoFocus}
              onInteractOutside={(e) => {
                e.preventDefault();
                if (enquiryModalIsOpen) {
                  return setEnquiryModalState(null);
                }
                handleCloseDatePicker();
              }}
              className={cn(
                'DatePickerContent',
                isOneDate
                  ? 'mt-auto mb-auto !w-full md:!max-w-[500px]'
                  : 'md:mt-[15vh] md:mb-[50px]',
                {
                  UnmountDatePicker: willUnmount,
                  DatePickerContentWithSummary: showSummary,
                },
              )}>
              {!enquiryModalIsOpen && (
                <button onClick={handleCloseDatePicker} className="DialogClose">
                  <XIcon className="w-6 h-6 m-auto fill-current text-gray-500 z-50" />
                </button>
              )}

              <div className="relative">
                <DatePickerHeader
                  showSummary={showSummary}
                  dict={dict}
                  rangeValue={rangeValue}
                  lang={lang}
                  isOneDate={isOneDate}
                />
                {canRenderError && (
                  <DatePickerError
                    dict={dict}
                    village={village}
                    selectedDates={rangeValue}
                  />
                )}
                {canShowPromotionMessage && <DatePickerSucessMessage />}
                <div className={isOneDate ? 'bg-white py-10' : 'bg-white'}>
                  {shouldInvalidateDates ? (
                    <BookingCalendar
                      shouldFetchUnavailableDates={datePickerIsOpen ?? false}
                      closeModal={onOpenChange}
                      village={village}
                      experienceId={experienceId}
                      confirmedDates={confirmedDates}
                      rangeValue={rangeValue}
                      onRangeValueChange={setRangeValue}
                      lang={lang}
                      isOneDate={isOneDate}
                      extraDicts={extraDicts}
                      skipCheckInDaysValidation={skipCheckInDaysValidation}
                    />
                  ) : (
                    <Calendar
                      confirmedDates={confirmedDates}
                      rangeValue={rangeValue}
                      lang={lang}
                      isOneDate={isOneDate}
                      onRangeValueChange={setRangeValue}
                    />
                  )}
                  {!isOneDate && (
                    <DatePickerFooter
                      dict={dict}
                      onConfirmDates={handleConfirmDates}
                      rangeValue={rangeValue}
                      clearDates={handleClearDates}
                      village={village}
                      showSummary={showSummary}
                      lang={lang}
                    />
                  )}
                </div>
              </div>
            </Content>
          </div>
        ) : (
          <Content
            onOpenAutoFocus={handleOpenAutoFocus}
            onInteractOutside={(e) => {
              e.preventDefault();
              handleCloseDatePicker();
            }}
            className={cn(
              'DatePickerContent md:mt-[15vh] md:mb-[50px] desktop:mt-0',
              {
                'mt-auto mb-auto !w-full md:!max-w-[500px]': isOneDate,
              },
              {
                UnmountDatePicker: willUnmount,
              },
            )}>
            <button onClick={handleCloseDatePicker} className="DialogClose">
              <XIcon className="w-6 h-6 m-auto fill-current text-gray-500 z-50" />
            </button>
            <DatePickerHeader
              showSummary={showSummary}
              dict={dict}
              rangeValue={rangeValue}
              lang={lang}
              isOneDate={isOneDate}
            />
            {canRenderError && (
              <DatePickerError
                dict={dict}
                village={village}
                selectedDates={rangeValue}
              />
            )}
            {canShowPromotionMessage && <DatePickerSucessMessage />}
            <div className={isOneDate ? 'bg-white py-10' : 'bg-white'}>
              {shouldInvalidateDates ? (
                <BookingCalendar
                  shouldFetchUnavailableDates={datePickerIsOpen ?? false}
                  closeModal={onOpenChange}
                  village={village}
                  experienceId={experienceId}
                  confirmedDates={confirmedDates}
                  rangeValue={rangeValue}
                  onRangeValueChange={setRangeValue}
                  lang={lang}
                  isOneDate={isOneDate}
                  inVillaRange={inVillaRange}
                  totalExperienceRange={totalExperienceRange}
                  extraDicts={extraDicts}
                  skipCheckInDaysValidation={skipCheckInDaysValidation}
                />
              ) : (
                <Calendar
                  confirmedDates={confirmedDates}
                  rangeValue={rangeValue}
                  lang={lang}
                  isOneDate={isOneDate}
                  onRangeValueChange={setRangeValue}
                />
              )}
              {!isOneDate && (
                <DatePickerFooter
                  dict={dict}
                  onConfirmDates={handleConfirmDates}
                  rangeValue={rangeValue}
                  clearDates={handleClearDates}
                  village={village}
                  showSummary={showSummary}
                  lang={lang}
                />
              )}
            </div>
          </Content>
        )}
      </Overlay>
    </Portal>
  );
}
